import React, { HTMLProps, useEffect, useMemo, useRef, useState } from "react";
import { FormikErrors, FormikTouched } from "formik";
import { IconBaseProps, IconType } from "react-icons";

import { Container, CorrectIcon, ErrorText, LabelText } from "./styles";

type TextSelectProps = Omit<HTMLProps<HTMLSelectElement>, "placeholder"> & {
  LeadingIcon?: IconType | any;
  propsLeadingIcon?: IconBaseProps;
  TrailingIcon?: IconType | any;
  propsTrailingIcon?: IconBaseProps;
  errorMessage?: string | FormikErrors<any>;
  errorCondition?: boolean | FormikTouched<any>;
  isCorrect?: boolean;
  placeholder?: string;
};

const TextSelect = ({
  LeadingIcon,
  propsLeadingIcon,
  TrailingIcon,
  propsTrailingIcon,
  label,
  errorMessage,
  name,
  errorCondition,
  placeholder,
  isCorrect,
  disabled,
  children,
  type: _type,
  ...rest
}: TextSelectProps): JSX.Element => {
  const inputRef = useRef<HTMLSelectElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [isLabelActivated, setIsLabelActivated] = useState(false);

  useEffect(() => {
    setIsErrored(!!errorCondition);
  }, [errorCondition]);

  useEffect(() => {
    if (!inputRef.current) return undefined;
    const el = inputRef.current;
    const onBlur = (): void => setIsFocused(false);
    const onFocus = (): void => setIsFocused(true);
    el.addEventListener("blur", onBlur);
    el.addEventListener("focus", onFocus);

    return () => {
      el.removeEventListener("blur", onBlur);
      el.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    const val = inputRef.current?.value ?? "";
    setIsLabelActivated(isFocused || val.length > 0);
  }, [isFocused]);

  useEffect(() => {
    const val = inputRef.current?.value ?? "";
    setIsLabelActivated(val.length > 0);
  }, [rest]);

  const randomId = useMemo(() => {
    const text = label || "";
    return `${text.replace(/\s/g, "")}-${Math.floor(Math.random() * 100)}`;
  }, [label]);

  const LeftIcon = useMemo(() => {
    switch (typeof LeadingIcon) {
      case "function":
        return LeadingIcon();
      case "object":
        return LeadingIcon;
      default:
        return LeadingIcon ? <LeadingIcon {...propsLeadingIcon} /> : null;
    }
  }, [LeadingIcon, propsLeadingIcon]);

  const RightIcon = useMemo(() => {
    switch (typeof TrailingIcon) {
      case "function":
        return TrailingIcon();
      case "object":
        return TrailingIcon;
      default:
        return TrailingIcon ? <TrailingIcon {...propsTrailingIcon} /> : null;
    }
  }, [TrailingIcon, propsTrailingIcon]);

  const hasValue =
    (rest as any).value !== undefined &&
    (rest as any).value !== null &&
    (rest as any).value !== "";
  const defaultValue = !hasValue ? "" : (rest as any).defaultValue;

  return (
    <>
      <Container
        isFocused={isFocused}
        isErrored={isErrored}
        disabled={disabled}
      >
        {LeadingIcon && LeftIcon}
        <div>
          <LabelText
            labelActivated={isLabelActivated}
            id={`labelId-${randomId}`}
          >
            {label}
          </LabelText>

          <select
            id="30"
            name={name || _type}
            ref={inputRef}
            aria-labelledby={`labelId-${randomId}`}
            aria-controls={`control-${randomId}`}
            aria-describedby={`control-${randomId}`}
            disabled={disabled}
            {...rest}
            {...(!hasValue ? { defaultValue } : {})}
          >
            {placeholder && (
              <option value="" disabled hidden>
                {placeholder}
              </option>
            )}
            {children}
          </select>
        </div>

        {inputRef.current?.value && isCorrect && <CorrectIcon size={20} />}
        {TrailingIcon && RightIcon}
      </Container>

      {errorMessage && errorCondition && (
        <ErrorText aria-hidden="true" id={`control-${randomId}`}>
          {`${errorMessage}`}
        </ErrorText>
      )}
    </>
  );
};

export default TextSelect;
